import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import {
  PatientListItem,
  patientLastSeenTranslationMapping,
  type PatientLastSeen,
  patientLastSeenColor,
} from "@/models/PatientSummaryModel.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type LastSeenColumnProps = CellContext<PatientListItem, PatientLastSeen>;

export const LastSeenColumn: React.FC<LastSeenColumnProps> = ({ getValue }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const lastSeen = getValue();

  if (lastSeen === "unknown") {
    return null;
  }

  return (
    <div className={styles.container}>
      <Tag
        label={t(patientLastSeenTranslationMapping(lastSeen))}
        color={patientLastSeenColor(lastSeen)}
        data-component="last-seen-tag"
        data-bucket={lastSeen}
      />
    </div>
  );
};

export const LastSeenHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.lastSeen.header")}</Typography>
    </div>
  );
};

const lastSeenRank: Record<PatientLastSeen | "never", number> = {
  recent: 3,
  olderThanTwoWeeks: 2,
  olderThanAMonth: 1,
  never: 0,
  unknown: 4,
};

export const sortByLastSeen: SortingFn<PatientListItem> = (rowA, rowB) => {
  const lastSeenA = rowA.original.patientLastSeen;
  const lastSeenB = rowB.original.patientLastSeen;
  return lastSeenRank[lastSeenA] - lastSeenRank[lastSeenB];
};

const makeStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
